import React from 'react'
import { Spinner } from 'reactstrap'
import { Info, ChevronUp } from 'lucide-react'
import flags from '../../common/flag'
import { getLocalesForCountry } from '../../utils/insightsLocale'
import FlagSelectDropdown from '../Common/FlagSelectDropdown'
import BackButton from '../Common/BackButton'
import './insightsPageHeader.css'

/**
 * The header block shared by every Weekly Insights page (Trending, New Release, Top
 * Apps, Top Discounts, Pre-Orders, and — minus the platform/country pickers — Popular):
 * title, country/language pickers, description, disclaimer, applied-filter badges,
 * last-updated timestamp, and the platform selector + Filters/Sort controls row.
 *
 * Each page still owns its own data fetching and filter state; this component is pure
 * presentation; it just stops six pages from independently hand-rolling (and
 * independently breaking) the same layout. All the props below are optional except
 * `title` — a page that doesn't need platform pickers, applied-filter badges, etc.
 * simply omits those props and that piece of the header doesn't render.
 *
 * Layout note: the platform selector lives in the controls row (alongside
 * Filters/Sort, on the left) rather than next to the title. A long title (e.g. "Top
 * Selling – Weekly Earners") plus platform pills plus country/language pickers all
 * competing for the same line pushed the country/language pickers onto their own
 * wrapped row unpredictably; keeping the title row to just the title (and country/
 * language pickers) avoids that, and the controls row wraps too
 * (`flex-wrap: wrap` in insightsPageHeader.css) so platform pills and Filters/Sort
 * stack cleanly instead of overlapping at in-between viewport widths.
 */
export default function InsightsPageHeader({
  title,
  description,
  disclaimer,

  // Only set by pages reached via a Weekly Insights "View All" click — renders a
  // back button above the title that returns to the Weekly Insights dashboard.
  // Landing here directly (sidebar link, bookmark, etc.) leaves this unset, so no
  // back button shows.
  onBack,

  // Arbitrary custom control rendered on the same wrapping row as the title (e.g.
  // Popular's plain "sort by revenue/downloads" <select>, for a page too simple to
  // need the full platform/country picker treatment).
  topExtra,

  // Platform selector (e.g. Meta Quest / Steam)
  platforms,
  platformsLoading,
  selectedPlatform,
  onPlatformChange,
  // Platform ids to leave out of the picker entirely — reserve this for a
  // platform that doesn't apply to this page at all, not one that's simply
  // not supported yet (use `disabledPlatforms` for that instead, so the app
  // still communicates every platform it supports).
  excludePlatforms = [],
  // Platform ids to keep visible but non-interactive (e.g. a platform whose
  // data isn't available yet). Clicks are ignored and a "Coming soon" tooltip
  // shows on hover/click — same behaviour as the shared `PlatformTabs`.
  disabledPlatforms = [],
  disabledPlatformTooltip = 'Coming soon',

  // Country / language pickers
  countriesList,
  countryOptions,
  countriesLoading,
  selectedCountry,
  selectedLanguage,
  onCountryChange, // (countryCode, languageForCountry) => void
  onLanguageChange, // (languageCode) => void

  // Applied filter chips
  appliedFilters,
  onRemoveFilter,

  lastUpdated,

  // Filters + Sort row (omit either to hide that control)
  onOpenFilters,
  rankSort, // null | 'asc' | 'desc'
  onToggleSort,

  // Extra content rendered below everything else (e.g. a section title + tabs)
  children,
}) {
  const [hoverRank, setHoverRank] = React.useState(false)
  const hasPlatforms = Array.isArray(platforms)
  const hasLocale = Array.isArray(countryOptions)
  const filterEntries = Object.entries(appliedFilters || {})

  return (
    <div className={`insights-header${onBack ? ' insights-header--with-back' : ''}`}>
      {onBack ? (
        <div className="insights-header__back">
          <BackButton onClick={onBack} label="Back" />
        </div>
      ) : null}

      <div className="insights-header__top">
        <h1 className="insights-header__title">{title}</h1>

        {topExtra}

        {hasLocale ? (
          <div className="insights-header__locale">
            <FlagSelectDropdown
              options={countryOptions}
              value={selectedCountry}
              loading={countriesLoading}
              placeholder="Select Country"
              onChange={(code) => {
                const langForCountry = getLocalesForCountry(countriesList, code)[0]?.value || selectedLanguage || null
                onCountryChange?.(code, langForCountry)
              }}
              icon={(code) => {
                const FlagComp = flags[code]
                return FlagComp ? <FlagComp style={{ width: '16px', height: 'auto' }} /> : null
              }}
            />

            <FlagSelectDropdown
              options={getLocalesForCountry(countriesList, selectedCountry)}
              value={selectedLanguage}
              loading={countriesLoading}
              placeholder="Select Language"
              onChange={(val) => onLanguageChange?.(val)}
              noOptionsText="Select a country first"
            />
          </div>
        ) : null}
      </div>

      {description ? <p className="insights-header__description">{description}</p> : null}

      {disclaimer ? (
        <p className="insights-header__disclaimer">
          <Info size={16} color="#A1ACB8" />
          <span>{disclaimer}</span>
        </p>
      ) : null}

      {filterEntries.length > 0 ? (
        <div className="insights-header__filter-chips">
          {filterEntries.map(([key, value]) => {
            const displayVal = Array.isArray(value)
              ? value.join(', ')
              : value === null || value === undefined || value === ''
              ? 'Any'
              : String(value)
            return (
              <span key={key} className="insights-header__filter-chip">
                <span className="insights-header__filter-chip-key">{key.replace(/_/g, ' ')}:</span>
                <span>{displayVal}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    onRemoveFilter?.(key)
                  }}
                  aria-label={`Remove filter ${key}`}
                  className="insights-header__filter-chip-remove"
                >
                  ×
                </button>
              </span>
            )
          })}
        </div>
      ) : null}

      {lastUpdated ? (
        <p className="insights-header__last-updated">
          Last updated:{' '}
          {lastUpdated.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      ) : null}

      {hasPlatforms || onOpenFilters || onToggleSort ? (
        <div className="insights-header__controls">
          {hasPlatforms ? (
            <div className="insights-header__platforms">
              {platformsLoading ? (
                <Spinner size="sm" color="light" />
              ) : (
                platforms
                  .filter((p) => p.active && !excludePlatforms.includes(p.id))
                  .map((p) => {
                    const isDisabled = disabledPlatforms.includes(p.id)
                    const button = (
                      <button
                        key={p.id}
                        type="button"
                        aria-disabled={isDisabled}
                        className={`insights-header__platform-btn${selectedPlatform === p.id ? ' is-active' : ''}${isDisabled ? ' insights-header__platform-btn--disabled' : ''}`}
                        onClick={() => {
                          if (isDisabled) return
                          onPlatformChange?.(p.id)
                        }}
                      >
                        {p.icon_url && (
                          <img src={p.icon_url} alt={p.display_name} className="insights-header__platform-icon" />
                        )}
                        {p.display_name}
                      </button>
                    )

                    if (!isDisabled) return button

                    return (
                      <span
                        key={p.id}
                        className="insights-header__platform-tooltip-wrap"
                        data-tooltip={disabledPlatformTooltip}
                      >
                        {button}
                      </span>
                    )
                  })
              )}
            </div>
          ) : null}

          {onOpenFilters || onToggleSort ? (
            <div className="insights-header__controls-right">
              {onOpenFilters ? (
                <button type="button" className="insights-header__filters-btn" onClick={onOpenFilters}>
                  <i className="fa fa-filter" />
                  <span>Filters</span>
                </button>
              ) : null}

              {onToggleSort ? (
                <span
                  role="button"
                  aria-label="Sort by rank"
                  title={
                    rankSort
                      ? `Sort by rank (${rankSort === 'asc' ? 'Ascending' : 'Descending'})`
                      : 'Sort by rank (toggle asc/desc)'
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    onToggleSort()
                  }}
                  onMouseEnter={() => setHoverRank(true)}
                  onMouseLeave={() => setHoverRank(false)}
                  className={`insights-header__sort-btn${rankSort ? ' is-active' : ''}${hoverRank ? ' is-hovered' : ''}`}
                >
                  <span>Sort by</span>
                  <ChevronUp
                    size={18}
                    color={rankSort ? '#e83e8c' : hoverRank ? '#ff6fa3' : '#A1ACB8'}
                    style={{
                      transform: rankSort === 'desc' ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 160ms ease, color 160ms ease',
                    }}
                  />
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {children}
    </div>
  )
}
